import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { Tooltip, Box, Typography, Grid } from '@mui/material';
import { Title, Text, LineChart, PieChart, PieChartGetColor } from 'shared/components';
import { useSelector, useDispatch } from 'react-redux';
import CardDetails from '../CardDetails';
import { SEGMENT_FILTER_FIELD } from '../../utils/cube/constants';
import { getDetails } from '../../actions';
import { Container, Col, Item, Row, Scroller, YearRow, TextContainer } from './styles';
import { TitleCard, LinkMore, TitleTotal } from '../styles';

export const DonationsCard = React.memo(({ total, rows, years, settings, expanded }) => {
	const dispatch = useDispatch();
	const { loading, list } = useSelector((state) => state.details);
	const { accountId } = useSelector((state) => state.profile.me);
	const [ openDetails, setOpenDetails ] = React.useState(false);
	const [ showMore, setShowMore ] = React.useState({});
	
	const handleCloseMoreDetails = () => {
		setOpenDetails(false);
	};

	const handleOnClickSegment = (segment) => {
		setOpenDetails(false);
		setTimeout(() => {
			setOpenDetails(true);
		}, 100);
		const field = SEGMENT_FILTER_FIELD[settings.segment];
		const options = { accountId };
		options[field] = segment;
		dispatch(getDetails(options));
	};

	const getChart = (segment, percentage) => {
		const { title: name, rawTotal: value } = segment;
		return {
			name,
			percentage,
			value
		};
	};

	const getPercentage = (row, segment) => {
		const percentage = segment.rawTotal / row.total * 100;
		return `${numeral(percentage).format('0.00')}%`;
	};

	const getItem = (id, segment, row) => {
		const percentage = getPercentage(row, segment);
		const average = row.total / row.segments.length;
		return (
			<Item key={id} onClick={() => handleOnClickSegment(segment.title)}>
				<PieChart basicData={getChart(segment, percentage)} percentage={percentage} average={average} />
				<TextContainer>
					<Tooltip title={segment.title} arrow>
						<Box
							style={{ margin: 0 }}
							component="div"
							my={2}
							textOverflow="ellipsis"
							overflow="hidden"
						>
							{segment.title}
						</Box>
					</Tooltip>
					<Box
						style={{ margin: 0 }}
						component="div"
						my={2}
						textOverflow="ellipsis"
						overflow="hidden"
					>
						{segment.total}
					</Box>
				</TextContainer>
			</Item>
		);
	};

	const handleShowMore = (blockId) => {
		const status = !(showMore[blockId]);
		return setShowMore({[blockId]: status});
	};

	const isEmpty = (total === 0);

	return (
		<Container expanded={expanded}>
			{ isEmpty && (
				<Grid container direction="row" justify="center" alignItems="center" spacing={1}>
					<Text style={{ width: '100%', marginTop: 160 }} centered>
						No data matching your filters, please change your card settings and try again.
					</Text>
				</Grid>
			)}
			{ !isEmpty && (
				<Row>
					<Col>
						<TitleTotal gutterBottom>
							Total: ${`${numeral(total).format(0, 0)}`}
						</TitleTotal>
					</Col>
					{total !== 0 && (
						<Col>
							<LineChart height="110px" data={years} total={total} showTotal={false} />
						</Col>
					)}
				</Row>
			)}
			{ !isEmpty && rows.map((row, idx) => (
				<YearRow key={idx}>
					<TitleCard gutterBottom>{row.title}</TitleCard>
					{!row.segments ||
						(row.segments.length === 0 && <Text>There is no data matching your filters</Text>)}
					{row.segments.length !== 0 && (
						<Scroller>
							{(showMore[row.title] ? row.segments : row.segments.slice(0, 6))
								.map((segment, sidx) => getItem(sidx, segment, row))}
							{(row.segments.length > 6) && !showMore[row.title] && (
								<LinkMore onClick={() => handleShowMore(row.title)}>Show {row.segments.length - 6} more...</LinkMore>
							)}
							{showMore[row.title] && (<LinkMore onClick={() => handleShowMore(row.title)}>Show Less</LinkMore>)}
						</Scroller>
					)}
					{openDetails && (
						<CardDetails loading={loading} list={list} onClose={() => handleCloseMoreDetails()} />
					)}
				</YearRow>
			))}
		</Container>
	);
});

DonationsCard.propTypes = {
	total: PropTypes.number,
	rows: PropTypes.array,
	years: PropTypes.array,
	settings: PropTypes.object
};
